import React, { useState, useRef } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Database, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Import() {
  const { user } = useAuth();
  const canImport = ["admin", "supervisor"].includes(user?.role);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [template, setTemplate] = useState("ayolinx");
  const [customerType, setCustomerType] = useState("FOREX");
  const [system, setSystem] = useState("TARAPAY");
  const [selected, setSelected] = useState({});
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setResult(null); setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      // pre-select all non-trivial sheets
      const sel = {};
      data.sheets.forEach((s) => { sel[s.sheet] = s.row_count > 0; });
      setSelected(sel);
      // auto-detect template
      const sheetNames = data.sheets.map((s) => s.sheet.toLowerCase());
      if (sheetNames.some((n) => n.includes("qris issues") || n.includes("va issues"))) setTemplate("ayolinx");
      else setTemplate("tarapay");
      // auto-detect system from filename / content
      if (data.detected_system?.system) setSystem(data.detected_system.system);
    } catch (e2) {
      setErr(formatErr(e2));
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const sheetsCsv = Object.entries(selected).filter(([, v]) => v).map(([k]) => k).join(",");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("template", template);
      fd.append("sheets", sheetsCsv);
      fd.append("customer_type", customerType);
      fd.append("system", system);
      fd.append("skip_duplicates", skipDup ? "true" : "false");
      const { data } = await api.post("/import/tickets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e2) {
      setErr(formatErr(e2));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setErr(""); setSelected({});
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!canImport) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="import-forbidden">Hanya admin/supervisor yang dapat mengimpor data.</div>;
  }

  const downloadTemplate = async (kind) => {
    try {
      const tok = localStorage.getItem("tara_token");
      const res = await fetch(`${API_BASE}/import/template?template=${kind}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orbit_${kind}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(`Gagal download template: ${e.message}`);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl animate-slide-up" data-testid="import-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Data Migration</div>
          <h1 className="text-2xl font-bold tracking-tight">Bulk Import from Excel</h1>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Import histori tracking dari spreadsheet <span className="font-mono text-white">Ayolinx Transaction Issue</span> (sheet &quot;VA Issues&quot; / &quot;QRIS Issues&quot;)
            atau <span className="font-mono text-white">Tarapay Tracking Work List</span> (sheet per-merchant). Status diterjemahkan ke ticket lifecycle:
            Success → Closed, Failed → Closed, Pending → Escalated to Bank. Duplikasi (order/RRN/VA sama) otomatis di-skip.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0" data-testid="template-download-group">
          <button
            data-testid="download-ayolinx-template"
            onClick={() => downloadTemplate("ayolinx")}
            className="bg-surface border border-line hover:border-cyan/50 hover:bg-surface2 text-xs font-semibold px-3 py-2 rounded-sm flex items-center gap-2 transition-colors"
            title="Download template Ayolinx (QRIS Issues + VA Issues)"
          >
            <Download className="w-3.5 h-3.5 text-cyan" />
            Template Ayolinx
          </button>
          <button
            data-testid="download-tarapay-template"
            onClick={() => downloadTemplate("tarapay")}
            className="bg-surface border border-line hover:border-cyan/50 hover:bg-surface2 text-xs font-semibold px-3 py-2 rounded-sm flex items-center gap-2 transition-colors"
            title="Download template Tarapay (sheet per merchant)"
          >
            <Download className="w-3.5 h-3.5 text-cyan" />
            Template Tarapay
          </button>
        </div>
      </div>

      <Panel title="Step 1 — Upload File" testid="step-upload">
        {!file && (
          <>
            <label className="block border-2 border-dashed border-line rounded-sm p-10 text-center cursor-pointer hover:border-cyan/50 hover:bg-surface2/30 transition-colors" data-testid="upload-dropzone">
              <Upload className="w-8 h-8 mx-auto mb-3 text-slate-500" />
              <div className="text-sm font-medium mb-1">Click to choose .xlsx file</div>
              <div className="text-xs text-slate-500">Max 20 MB · Ayolinx Ops or Tarapay Tracking template</div>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={onPickFile} className="hidden" data-testid="upload-file-input" />
            </label>
            <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-2 flex-wrap">
              <span>Belum punya template?</span>
              <button data-testid="dropzone-dl-ayolinx" onClick={() => downloadTemplate("ayolinx")} className="text-cyan hover:underline inline-flex items-center gap-1">
                <Download className="w-3 h-3" /> Ayolinx
              </button>
              <span className="text-slate-700">·</span>
              <button data-testid="dropzone-dl-tarapay" onClick={() => downloadTemplate("tarapay")} className="text-cyan hover:underline inline-flex items-center gap-1">
                <Download className="w-3 h-3" /> Tarapay
              </button>
            </div>
          </>
        )}
        {file && (
          <div className="flex items-center justify-between bg-surface2 border border-line rounded-sm p-3" data-testid="file-info">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-cyan" />
              <div>
                <div className="text-sm font-medium">{file.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            </div>
            <button data-testid="reset-btn" onClick={reset} className="text-xs text-slate-400 hover:text-rose-400 border border-line px-3 py-1 rounded-sm">Reset</button>
          </div>
        )}
      </Panel>

      {err && <div data-testid="import-err" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}

      {preview && (
        <>
          <Panel title="Step 2 — Configure" testid="step-configure">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">System</label>
                <select data-testid="system-select" value={system} onChange={(e) => setSystem(e.target.value)} className={ipt}>
                  <option value="TARAPAY">Tarapay</option>
                  <option value="SATURNUS">Saturnuspay</option>
                </select>
                {preview.detected_system && (
                  <div className="text-[10px] text-cyan mt-1" data-testid="detected-system">
                    Auto-detected: <span className="font-mono font-semibold">{preview.detected_system.system}</span> ({preview.detected_system.reason})
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Template</label>
                <select data-testid="template-select" value={template} onChange={(e) => setTemplate(e.target.value)} className={ipt}>
                  <option value="ayolinx">Ayolinx Ops (VA Issues / QRIS Issues)</option>
                  <option value="tarapay">Per-Merchant Sheets (sheet name = merchant)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Default Customer Type</label>
                <select data-testid="customer-type-select" value={customerType} onChange={(e) => setCustomerType(e.target.value)} className={ipt}>
                  <option value="FOREX">Forex</option>
                  <option value="GAMING">Gaming</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer">
              <input data-testid="skip-dup-toggle" type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} className="accent-cyan" />
              Skip duplicates (Order Number / RRN / VA already exists)
            </label>
          </Panel>

          <Panel title={`Step 3 — Select Sheets (${preview.sheets.length})`} testid="step-sheets">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {preview.sheets.map((s) => (
                <label key={s.sheet} className="flex items-start gap-3 border border-line rounded-sm p-3 cursor-pointer hover:bg-surface2" data-testid={`sheet-row-${s.sheet}`}>
                  <input
                    type="checkbox"
                    checked={!!selected[s.sheet]}
                    onChange={(e) => setSelected({ ...selected, [s.sheet]: e.target.checked })}
                    disabled={s.row_count === 0}
                    className="accent-cyan mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium font-mono">{s.sheet}</span>
                      <Badge color={s.row_count > 0 ? "info" : "muted"}>{s.row_count} rows</Badge>
                    </div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5">
                      {(s.headers || []).slice(0, 6).filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="select-all-btn" onClick={() => { const sel = {}; preview.sheets.forEach((s) => sel[s.sheet] = s.row_count > 0); setSelected(sel); }} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2">Select all</button>
              <button data-testid="clear-all-btn" onClick={() => setSelected({})} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2">Clear</button>
            </div>
          </Panel>

          <div className="flex justify-end">
            <button
              data-testid="run-import-btn"
              onClick={submit}
              disabled={busy || Object.values(selected).every((v) => !v)}
              className="bg-cyan text-bg font-semibold px-6 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-50"
            >
              <Database className="w-4 h-4" />
              {busy ? "Importing..." : "Run Import"}
            </button>
          </div>
        </>
      )}

      {result && (
        <Panel title="Import Result" testid="import-result">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400">Created</div>
              <div className="text-2xl font-bold font-mono text-emerald-400 mt-1" data-testid="result-created">{result.created}</div>
            </div>
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-400">Skipped (duplicates)</div>
              <div className="text-2xl font-bold font-mono text-amber-400 mt-1" data-testid="result-skipped">{result.skipped}</div>
            </div>
            <div className="border border-line bg-surface2 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Total Parsed</div>
              <div className="text-2xl font-bold font-mono text-white mt-1">{result.total_rows_parsed}</div>
            </div>
          </div>

          {Object.keys(result.by_sheet).length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Per Sheet</div>
              {Object.entries(result.by_sheet).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs border border-line rounded-sm px-3 py-1.5">
                  <span className="font-mono">{k}</span>
                  <span className="text-slate-400">
                    <span className="text-emerald-400">+{v.created}</span> created · <span className="text-amber-400">{v.skipped}</span> skipped
                  </span>
                </div>
              ))}
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="mt-3 border border-rose-500/30 bg-rose-500/5 rounded-sm p-3">
              <div className="flex items-center gap-2 text-rose-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-xs font-semibold">{result.errors.length} errors</span>
              </div>
              <div className="text-[10px] font-mono space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => <div key={i} className="text-slate-400">{e}</div>)}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 text-emerald-400 text-xs">
            <CheckCircle2 className="w-4 h-4" />
            Import complete. Tickets sekarang tersedia di halaman Tickets dengan SLA timer aktif sesuai histori.
          </div>
        </Panel>
      )}
    </div>
  );
}
